/**
 * KnowledgeHubOnboarding — full-screen modal shown once per user on first Knowledge Hub visit.
 * AC7: Plays brand video (if available). Dismissed on button click or video end.
 * Uses localStorage key 'knowledgeHubOnboardingSeen' to show only once.
 */
import { useRef } from 'react';
import { X } from 'lucide-react';

const FMA_DISCLAIMER =
  'Marome Investments (Pty) Ltd is an authorised FSP. Past performance is not indicative of future results.';

interface KnowledgeHubOnboardingProps {
  videoUrl?: string;
  onDismiss: () => void;
}

const KnowledgeHubOnboarding: React.FC<KnowledgeHubOnboardingProps> = ({ videoUrl, onDismiss }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleDismiss = () => {
    localStorage.setItem('knowledgeHubOnboardingSeen', 'true');
    onDismiss();
  };

  const handleVideoEnd = () => {
    handleDismiss();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to the Knowledge Hub"
    >
      <div className="relative w-full max-w-3xl mx-4">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors"
          aria-label="Close onboarding"
        >
          <X size={24} />
        </button>

        <div className="relative rounded-2xl overflow-hidden bg-black">
          {videoUrl ? (
            <>
              <video
                ref={videoRef}
                src={videoUrl}
                autoPlay
                muted
                playsInline
                className="w-full"
                onEnded={handleVideoEnd}
              />
              {/* FMA disclaimer */}
              <div className="absolute bottom-16 left-0 right-0 bg-black/60 text-white text-xs px-4 py-2">
                {FMA_DISCLAIMER}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
              <div className="w-16 h-16 rounded-full bg-teal-500/20 flex items-center justify-center mb-4">
                <span className="text-3xl">🎓</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Welcome to the Knowledge Hub</h2>
              <p className="text-slate-400">
                Explore educational videos, glossary terms, and personalised learning content.
              </p>
            </div>
          )}

          <button
            onClick={handleDismiss}
            className="block w-full py-4 bg-teal-500 hover:bg-teal-400 text-white font-bold text-sm transition-colors"
          >
            Start Exploring
          </button>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeHubOnboarding;
